import { AppShell, ActionIcon, Group, NumberFormatter, Popover, Tooltip } from '@mantine/core';
import { useUnit } from 'effector-react';
import { X, Menu, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { GameAside } from 'entities/game';
import { ViewerMenu, viewerModel } from 'entities/viewer';
import { useBreakpoint } from 'shared/lib';
import HelpIcon from 'shared/ui/assets/help-icon.svg?react';
import InfinityIcon from 'shared/ui/assets/infinity-icon.svg?react';
import { cashierLayoutModel } from '../../model';
import { CashierLayoutNavList } from '../cashier-layout-nav-list';
import { CashierLayoutOverlay } from '../cashier-layout-overlay';
import cls from './cashier-layout.module.scss';

const CashierLayoutBalanceHelp = () => {
	const { t } = useTranslation();
	const sm = useBreakpoint('sm');

	const content = (
		<div className={cls.cashierLayoutBalanceTooltip}>
			<div className={cls.cashierLayoutBalanceTooltipTitle}>
				{t('cashier-layout.available-balance')}
			</div>
			<div className={cls.cashierLayoutBalanceTooltipContent}>
				{t('cashier-layout.available-balance-description')}
			</div>
		</div>
	);

	const control = (
		<button
			type='button'
			className={cls.cashierLayoutBalanceHelp}
			data-test-id='cashier-layout-balance-help'
			aria-label={t('cashier-layout.available-balance')}
		>
			<HelpIcon />
		</button>
	);

	if (!sm) {
		return (
			<Popover
				width={280}
				position='bottom'
			>
				<Popover.Target>{control}</Popover.Target>
				<Popover.Dropdown>{content}</Popover.Dropdown>
			</Popover>
		);
	}

	return (
		<Tooltip
			multiline
			w={280}
			position='bottom'
			events={{ hover: true, focus: true, touch: true }}
			label={content}
		>
			{control}
		</Tooltip>
	);
};

export const CashierLayout = ({ children }: PropsWithChildren) => {
	const { t } = useTranslation();

	const [openedGames, cashdesk, cashdeskBalance] = useUnit([
		cashierLayoutModel.$openedGames,
		viewerModel.$cashdesk,
		cashierLayoutModel.$cashdeskBalance,
	]);

	const asideSwitcher = useUnit(cashierLayoutModel.asideSwitcher);

	const navbarSwitcher = useUnit(cashierLayoutModel.navbarSwitcher);

	return (
		<AppShell
			header={{ height: 60 }}
			navbar={{
				width: 300,
				breakpoint: 'lg',
				collapsed: { mobile: !navbarSwitcher.active, desktop: !navbarSwitcher.active },
			}}
			aside={{
				width: 300,
				breakpoint: 'lg',
				collapsed: {
					mobile: !asideSwitcher.active,
					desktop: !asideSwitcher.active,
				},
			}}
			className={cls.cashierLayoutRoot}
		>
			<AppShell.Header className={cls.cashierLayoutHeader}>
				<div className={cls.cashierLayoutHeaderLeft}>
					<ActionIcon
						data-test-id='cashier-layout-navbar-toggle'
						className={cls.cashierLayoutBurger}
						hiddenFrom='sm'
						variant='outline'
						onClick={navbarSwitcher.toggle}
					>
						{navbarSwitcher.active ? (
							<X className={cls.cashierLayoutAsideIcon} />
						) : (
							<Menu className={cls.cashierLayoutAsideIcon} />
						)}
					</ActionIcon>
					<h3 className={cls.cashierLayoutTitle}>{t('cashier-layout.title')}</h3>
				</div>
				<CashierLayoutNavList visibleFrom='sm' />
				<Group
					gap={0}
					wrap='nowrap'
				>
					{cashdesk && (
						<div
							className={cls.cashierLayoutBalance}
							data-test-id='cashier-layout-balance'
						>
							{cashdeskBalance === null ? (
								<InfinityIcon className={cls.cashierLayoutBalanceInfinity} />
							) : (
								<NumberFormatter value={cashdeskBalance} />
							)}
							{` ${cashdesk.currency}`}
							<CashierLayoutBalanceHelp />
						</div>
					)}
					<Group
						gap={8}
						wrap='nowrap'
					>
						<ViewerMenu />
						<ActionIcon
							data-test-id='cashier-layout-aside-toggle'
							className={cls.cashierLayoutBurger}
							disabled={!openedGames}
							onClick={asideSwitcher.toggle}
						>
							{asideSwitcher.active ? (
								<PanelRightClose className={cls.cashierLayoutBurgerIcon} />
							) : (
								<PanelRightOpen className={cls.cashierLayoutBurgerIcon} />
							)}
						</ActionIcon>
					</Group>
				</Group>
			</AppShell.Header>
			<AppShell.Navbar className={cls.cashierLayoutNavbar}>
				<CashierLayoutNavList />
			</AppShell.Navbar>
			{children}
			<CashierLayoutOverlay hiddenFrom='lg' />
			{openedGames && (
				<AppShell.Aside className={cls.cashierLayoutAside}>
					<GameAside />
				</AppShell.Aside>
			)}
		</AppShell>
	);
};
